import { useEffect, useMemo, useRef, useState } from 'react'
import { NotificationType } from 'types/notification'
import {
    Attachment,
    InfoBox,
    InfoContent,
    InfoText,
    InfoTextTitle,
    InfoWrapper,
    InputInfoLabelSubTitle
} from '../styles'

import CountdownNotification from 'components/Countdown'
import { OutlinePersonIcon } from 'components/Icons/OutlinePersonIcon'
import { WarningModal } from 'components/Modals/WarningModal'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { notificationStatus } from 'constants/notificationStatus'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { AiOutlineExclamationCircle } from 'react-icons/ai'
import { MdOutlineAttachFile, MdOutlineClose } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { AuthenticationReducer, IAuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import {
    calculateTimeLeft,
    getCorIncidente,
    getDamage,
    getWarningCountdownColor
} from 'utils/damage'
import NotificationStatusAuthorization from '../../NotificationStatusAuthorization'
import { variableListNumberPatientsInvolved } from '../helper'
import { VariableReducer } from 'types/variable'
import NotificationService from 'services/Notification'
import { AttachmentProgressBar } from 'components/AttachmentProgressBar'


interface TopInfosProps {
    auth: IAuthenticationReducer
    notification: NotificationType
    setFileUploadS3Attached?: React.Dispatch<React.SetStateAction<File>>
    setUrlUploadS3AttachedFile?: React.Dispatch<React.SetStateAction<string>>
    setUrlDeleteS3AttachedFile?: React.Dispatch<React.SetStateAction<string>>
    control: Control<FieldValues, any>
    dirtyFields: Partial<FieldValues>
}

export const TopInfos = ({ auth, notification, setFileUploadS3Attached, setUrlUploadS3AttachedFile, setUrlDeleteS3AttachedFile, control, dirtyFields }: TopInfosProps) => {

    const authenticationSelector: AuthenticationReducer = useSelector((state: RootReducerState) => state.authenticationReducers)
    const variableSelector: VariableReducer = useSelector( (state: RootReducerState) => state.variableReducers)
    const [currentAttachmentFileName, setCurrentAttachmentFileName] = useState<string>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [selectedFileSize, setSelectedFileSize] = useState<number>(null);
    const [dragActive, setDragActive] = useState(false);
    const [warningModal, setWarningModal] = useState<boolean>(false)
    const [error, setError] = useState('');
    const [progressBarValue, setProgressBarValue] = useState<number>(0)

    // Cria uma referência para o input file
    const fileInputRef = useRef(null);

    useEffect(() => {
        const setAttachmentStateOnInitialize = async () => {     
            try {
                
                setProgressBarValue(1)
                dirtyFields.u_attachments_url = false;
                
                const {data}:any = await NotificationService.getPresignedUrlById(notification?.sys_id.value)
                if(data){
                    const response  = await NotificationService.getAttachmentBucketS3(data, setProgressBarValue)
                    
                    const file = new File([response], notification?.u_attachments_string?.value, { type: response.type || 'application/octet-stream' });

                    setSelectedFile(file);
                    setUrlUploadS3AttachedFile(notification?.u_attachments_url?.value)
                    setCurrentAttachmentFileName(notification?.u_attachments_string?.value)
                    setSelectedFileSize(Number(notification?.u_attachments_size?.value))
                    setFileUploadS3Attached(file)
                    setUrlDeleteS3AttachedFile(null)
                }
            } catch (error) {
                setError('O carregamento do anexo falhou. Verifique a sua conexão e tente novamente.');
                console.log('Ocorreu uma falha no carregamento do anexo: ', error)
            } finally {
                dirtyFields.u_attachments_url = true;
            }
        };
        
        if(auth?.dataJwt && (!selectedFile && notification?.u_attachments_url?.value)){
            setAttachmentStateOnInitialize();
        }

    }, [auth?.dataJwt, notification])

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        handleFile(file);
    };

    const getSizeFile = () => {
        if (selectedFileSize) {
            const sizeInKB = selectedFileSize / 1024;
            const sizeInMB = sizeInKB / 1024;
            if (sizeInMB >= 1) {
                return `${sizeInMB.toFixed(2)} MB`;
            }
            return `${sizeInKB.toFixed(2)} KB`;
        }
        return '';
    }

    const handleFile = (file: File) => {
        if (file) {
            const validExtensions = ['pdf', 'xls', 'xlsx', 'csv'];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!validExtensions.includes(fileExtension)) {
                setError('O arquivo selecionado não é de um tipo permitido. Por favor, selecione um arquivo .pdf, .xls, .xlsx ou .csv.');
                return;
            }

            const maxSize6Mb = 6 * 1024 * 1024;
            if (file.size > maxSize6Mb) {
                setError('O arquivo selecionado excede o tamanho máximo permitido de 6 MB.');
                return;
            }

            setSelectedFile(file)
        }
    };

    //será chamada ao clicar no botão
    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        handleFile(file);
    };

    useEffect(() => {            
            const uploadAttachmentToBucketS3 = async () => {
                try{
                    setProgressBarValue(1)
                    
                    let {url} = await NotificationService.createPresignedURL(selectedFile, setProgressBarValue);        
                    if(url){
                        dirtyFields.u_attachments_url = true;
                        setError('');
                        
                        setUrlUploadS3AttachedFile(url)
                        setFileUploadS3Attached(selectedFile)
                        setUrlDeleteS3AttachedFile(null)
                        setSelectedFileSize(selectedFile.size)
                        setCurrentAttachmentFileName(selectedFile.name);
                    } 
                }catch (e) {
                    dirtyFields.u_attachments_url = false;
                    setSelectedFile(null)
                    setError('O envio do anexo falhou. Verifique a sua conexão e tente novamente.');
                    
                    setUrlUploadS3AttachedFile(null)
                    setFileUploadS3Attached(null)
                    setUrlDeleteS3AttachedFile(null)
                    setSelectedFileSize(null)
                    setCurrentAttachmentFileName(null);
                  
                    console.log('Ocorreu uma falha no envio do anexo: ', e)
                }
            
        };

        if (selectedFile && !notification?.u_attachments_url?.value) {
            dirtyFields.u_attachments_url = false;
            uploadAttachmentToBucketS3();
        }
        
    }, [selectedFile])

    const removeAttachment = () => {
        setWarningModal(false)
        if (fileInputRef.current) {
            dirtyFields.u_attachments_url = true;
            setError(''); // Limpa qualquer mensagem de erro
            setCurrentAttachmentFileName(null)
            setSelectedFile(null)
            setUrlUploadS3AttachedFile(null)
            setUrlDeleteS3AttachedFile(notification?.u_attachments_url?.value)
            setSelectedFileSize(null)
            fileInputRef.current.value = '';
        }
    }

    let canUpdateFile = useMemo(() => {
        const isaCorporateQualityBoard = [listOfRoles.corporate_quality_board].includes(authenticationSelector?.dataJwt?.profile)
        if(isaCorporateQualityBoard)
            return notification?.u_reclassified_incident_type?.value

        return [listOfRoles.admin, listOfRoles.location_qa].includes(authenticationSelector?.dataJwt?.profile)
      }, [authenticationSelector?.dataJwt?.profile])


    const canSeeFile = useMemo(() => {
        return (notification?.u_state?.value != notificationStatus[4].value && notification?.u_state?.value != notificationStatus[5].value)
    }, [notification])

    const getFileExtension = (fileName: string) => {
        return fileName?.split('.').pop().toLowerCase();
    };

    const getFileNameWithoutExtension = (fileName: string) => {
        const parts = fileName?.split('.') || [];
        parts.pop();
        return parts.join('.');
    };

    const getAttachmentFileName = (filename: string) => {
        if (filename) {
            const onlyFilename = getFileNameWithoutExtension(filename)
            if (onlyFilename.length > 30) {
                const fileExtension = getFileExtension(filename)
                return onlyFilename.substring(0, 30).concat('...').concat(fileExtension)
            }
        }
        return filename
    }

    const handleClick = () => {        
        const downloadFile = () => {
            if (selectedFile) {
                const fileURL = URL.createObjectURL(selectedFile); 
                const link = document.createElement('a'); 
                link.href = fileURL;
                link.download = selectedFile.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link); 
                URL.revokeObjectURL(fileURL);
            }       
        }
        downloadFile();
    };

    return (
        <>
            <WarningModal
                icon={<AiOutlineExclamationCircle size={50} className="text-yellow" />}
                customStyle={{ titleStyle: 'text-yellow' }}
                title="Remover anexo"
                isOpen={warningModal as boolean}
                subTitle="Tem certeza que deseja remover o documento anexo da notificação?"
                leftButtonText="Remover anexo"
                leftButtonAction={() => removeAttachment()}
                handleCloseModal={() => setWarningModal(false)}
                rightButtonText="Manter anexo"
                rightButtonAction={() => setWarningModal(false)}
            />
            <InfoWrapper>
                <InfoBox>
                    <InfoContent>
                        <InfoTextTitle>Chave da notificação</InfoTextTitle>
                        <InfoText $bold>
                            {notification?.number?.display_value}
                        </InfoText>

                        <div className="mt-2">
                            <span
                                className={`chip-incidente incidente-${getCorIncidente(
                                    getDamage(notification?.damage_level?.value)
                                        ?.value
                                )}`}
                            >
                                {`${getDamage(notification?.damage_level?.value)
                                    ?.label
                                    }`}
                            </span>
                        </div>
                        <NotificationStatusAuthorization
                            notificationStatus={
                                notification?.u_state?.display_value
                            }
                            allowedStatus={[
                                notificationStatus[1].label,
                                notificationStatus[2].label
                            ]}
                            children={
                                <div className="mt-5">
                                    <span className="text-xs text-darkGray">
                                        Analisada e Encaminhada por
                                    </span>
                                    <p className="text-sm text-darkGray font-semibold">
                                        {notification?.forwarded_by?.display_value}
                                    </p>
                                </div>
                            }
                        />
                    </InfoContent>

                    <InfoContent>
                        <InfoTextTitle>Data da notificação</InfoTextTitle>

                        <InfoText>
                            {notification?.sys_created_on?.display_value ? (
                                notification?.sys_created_on?.display_value?.substring(
                                    0,
                                    notification?.sys_created_on?.display_value
                                        .length - 3
                                )
                            ) : (
                                <span>-</span>
                            )}
                        </InfoText>

                        <div className="mt-2">
                            <CountdownNotification
                                notification={notification}
                                containerStyle="flex flex-row justify-start"
                                status={notification?.u_state?.value}
                                color={`${getWarningCountdownColor(notification)}`}
                                timer={`${calculateTimeLeft(notification)}`}
                            />
                        </div>
                    </InfoContent>

                    <InfoContent>
                        <InfoTextTitle>Notificante</InfoTextTitle>
                        <InfoText className='max-w-[220px] break-words whitespace-normal'>
                            {(notification?.reporter_name?.display_value.length > 40
                                ? notification?.reporter_name?.display_value.substring(0, 40).concat('...')
                                : notification?.reporter_name?.display_value) ||
                                'Anônimo'}
                        </InfoText>
                        <NotificationStatusAuthorization
                            notificationStatus={
                                notification?.u_state?.display_value
                            }
                            allowedStatus={[notificationStatus[2].label]}
                            children={
                                <div className="mt-14">
                                    <span className="text-xs text-darkGray">
                                        Responsável
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <OutlinePersonIcon
                                            width="24"
                                            height="24"
                                            color="#893367"
                                        />
                                        <span className="text-base text-roxo font-semibold">
                                            {
                                                notification?.u_assigned_to
                                                    ?.display_value
                                            }
                                        </span>
                                    </div>
                                </div>
                            }
                        />
                    </InfoContent>

                    <InfoContent className='max-w-[450px]'>
                        { 
                        (canUpdateFile && canSeeFile)  ?
                            <>
                            <InfoTextTitle className='mb-1'>Anexo :</InfoTextTitle>
                                    { selectedFile ?
                                        <>
                                            <div className='h-[38px] shrink bg-[#EEEEF4] border-[1px] rounded-[8px] items-center inline-flex p-3 row justify-between cursor-pointer'>
                                                <div className='inline-flex items-center' onClick={handleClick}>
                                                    <div className='w-[18px] h-[18px] text-linkText pr-1'>
                                                        <MdOutlineAttachFile />
                                                    </div>
                                                    <div>
                                                        <span className='font-semibold text-xs text-darkGray' title={currentAttachmentFileName}>
                                                            {getAttachmentFileName(selectedFile.name)}
                                                        </span>
                                                        <span className='ml-1 text-xs text-[#474747]/60'> {getSizeFile()}</span>
                                                    </div>
                                                </div>
                                                <div className='justify-end'>
                                                    <MdOutlineClose onClick={() => setWarningModal(true)} />
                                                </div>
                                            </div>
                                            <AttachmentProgressBar progressBarValue={progressBarValue} />           
                                        </>
                                        :
                                        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={handleButtonClick}>
                                            <Attachment className={dragActive ? '' : 'border-dashed border-blueTheme border-[2px]'} >
                                                <MdOutlineAttachFile className="w-[18px] h-[18px]" />
                                                <span data-testid="test-notification-attachment" >
                                                    <span className='font-semibold text-xs text-darkGray'>Arraste e solte ou</span>
                                                    <span className="ml-1 text-sm underline font-semibold text-blueTheme">clique para procurar</span>
                                                </span>
                                            </Attachment>
                                        </div>
                                    }
                                    
                                    <InputInfoLabelSubTitle>Permitido apenas 1 arquivo nos formatos <span className='font-semibold'>pdf, xls, xlsx e csv</span>. Tamanho máximo de 6 MB.</InputInfoLabelSubTitle>
                                    {error &&
                                        <span className="text-errorText text-xs font-normal">
                                            {error}
                                        </span>
                                    }

                                    <Controller
                                        name="u_attachments_url"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="file"
                                                accept=".pdf,.xls,.xlsx,.csv"
                                                ref={fileInputRef}
                                                name={field.name}
                                                style={{ display: 'none' }}
                                                onChange={handleFileChange}
                                            />
                                        )}
                                        
                                    />
                            </>
                                :
                            <>
                               { notification?.u_attachments_url?.value && 
                                <>
                                    <InfoTextTitle className='mb-1'>Anexo :</InfoTextTitle>                                       
                                    <div className='text-linkText font-medium inline-flex items-center cursor-pointer'
                                        onClick={handleClick}>
                                        <MdOutlineAttachFile className="w-[18px] h-[18px]" ></MdOutlineAttachFile>
                                        <span className="text-sm font-semibold text-blueTheme ml-2" title={notification?.u_attachments_string?.value}>
                                            {getAttachmentFileName(notification?.u_attachments_string?.value)}
                                        </span>
                                    </div>
                                </>
                                }
                            </>
                        }
                    </InfoContent>
                </InfoBox>
            </InfoWrapper>
            {
                notification?.u_number_patients_involved?.value ===
                variableListNumberPatientsInvolved(variableSelector)?.choices?.[2]?.value
                &&
                <InfoWrapper>
                    <InfoContent>
                        <div className="mt-2">
                            <span className='text-darkGray text-[10px] bg-[#E5E5E5] px-2 rounded-[14px] flex items-center  justify-center gap-0 h-[21px] max-w-[299px]'>
                                <span className='text-xs flex-grow pl-2'>
                                    Essa é uma notificação em massa
                                </span>
                                <span className='text-xs font-semibold pl-1 pr-2'>
                                    x {notification?.u_notification_multiplier?.value}
                                </span>
                            </span>
                        </div>
                    </InfoContent>
                </InfoWrapper>
            }
        </>
    )
}
